import { getHeaders } from '../util';
import type {
  SearchCardsV2Request,
  SearchBubblesV2Request,
  WrappedCardResponse,
} from '../apiTypes';
import { baseUrl } from '../fetch';
import { ListingType } from '../../types/card/card_details_type';
import { SortByOptions } from '../../components/Shared/Constants/Browse/BrowseConstants';

export const searchCardsV2 = async (
  req: SearchCardsV2Request,
): Promise<WrappedCardResponse> => {
  const reqCopy = Object.assign({}, req);
  const ordering = req.ordering ?? 'distance';
  delete reqCopy.ordering;
  const r = await fetch(
    `${baseUrl || ''}cards/searchv2/?ordering=${ordering}`,
    {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reqCopy),
    },
  );
  return {
    status: r.status,
    statusText: r.statusText,
    headers: r.headers,
    body: await r.json(),
  };
};

export const hpfetchNextV2 = async (
  pagination: SearchCardsV2Request,
): Promise<WrappedCardResponse> => {
  return searchCardsV2(pagination);
};

export const summaryCardsV2 = async (
  req: SearchCardsV2Request,
): Promise<WrappedCardResponse> => {
  const reqCopy = Object.assign({}, req);
  const ordering = req.ordering ?? 'distance';
  delete reqCopy.ordering;
  const r = await fetch(
    `${baseUrl || ''}cards/summaryv2/?ordering=${ordering}`,
    {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reqCopy),
    },
  );

  return {
    status: r.status,
    statusText: r.statusText,
    headers: r.headers,
    body: await r.json(),
  };
};

export const bubbleCardsV2 = async (
  req: SearchBubblesV2Request,
): Promise<WrappedCardResponse> => {
  const reqCopy = Object.assign({}, req);
  const resolution = req.resolution;
  delete reqCopy.resolution;
  const r = await fetch(
    `${baseUrl || ''}cards/bubblesv2/?resolution=${resolution}`,
    {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(req),
    },
  );

  const responseBody = await r.json();
  const bubbleItems = [];
  for (const item of responseBody) {
    const bubble = Object.assign({}, item);
    delete bubble.listing_count;
    bubbleItems.push({
      ...bubble,
      card_count: item.listing_count,
    });
  }
  return {
    status: r.status,
    statusText: r.statusText,
    headers: r.headers,
    body: bubbleItems,
  };
};

export const countCardsV2 = async (
  req: SearchCardsV2Request,
): Promise<WrappedCardResponse> => {
  const r = await fetch(`${baseUrl || ''}cards/countv2/`, {
    method: 'POST',
    headers: {
      ...getHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(req),
  });

  return {
    status: r.status,
    statusText: r.statusText,
    headers: r.headers,
    body: await r.json(),
  };
};

export function createSearchRequest(
  pageSize: number,
  querySearchStringText: string,
  queryCategories?: string[],
  mapLocation?: { lng: number, lat: number },
  queryPostType?: string,
  searchSortBy: string,
  queryBounds?: {
    ne: { lat: number, lng: number },
    sw: { lat: number, lng: number },
  },
): SearchCardsV2Request {
  const request: SearchCardsV2Request = {
    limit: pageSize,
    search_text: querySearchStringText,
  };

  if (queryCategories) request.category_ids = queryCategories;

  if (mapLocation?.lng && mapLocation?.lat) {
    request.location = {
      lon: mapLocation.lng,
      lat: mapLocation.lat,
    };
  } else if (mapLocation?.coordinates?.lng && mapLocation?.coordinates?.lat) {
    request.location = {
      lon: mapLocation.coordinates.lng,
      lat: mapLocation.coordinates.lat,
    };
  } else if (searchSortBy === SortByOptions.Nearest) {
    request.location = { lat: 39.1, lon: -96.5 };
  }

  if (queryBounds) {
    request.in_bbox = {
      top_left: {
        lat: queryBounds.ne.lat,
        lon: queryBounds.sw.lng,
      },
      bottom_right: {
        lat: queryBounds.sw.lat,
        lon: queryBounds.ne.lng,
      },
    };
  }

  const possibleTypes: ListingType[] = ['supply', 'demand', 'storefront'];
  const listingType: ListingType[] = possibleTypes.filter(type =>
    queryPostType.includes(type),
  );
  request.listing_type = listingType;

  request.ordering =
    searchSortBy === SortByOptions.MostRecent ? '-updated' : 'distance';

  return request;
}
